// src/pages/components/CommentSection.tsx
import React, { useState, useRef, useEffect } from 'react';
import { tasksApi, TaskComment } from '../api/tasks';
import { useUserInfo } from '../hooks/useUserInfo';
import { usePermissionGuard } from '../hooks/usePermissionGuard';

interface CommentSectionProps {
  taskId: string;
  comments: TaskComment[];
  onRefresh: () => void;
}

function CommentSection({ taskId, comments, onRefresh }: CommentSectionProps) {
  const [newComment, setNewComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editText, setEditText] = useState('');
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [mentionSuggestions, setMentionSuggestions] = useState<string[]>([]);
  const [showMentions, setShowMentions] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const { currentUser, getUserName, getUserEmail, getAllUsersList } = useUserInfo();
  const { showError, showSuccess, guard } = usePermissionGuard();
  
  const allUsers = getAllUsersList();

  // Handle @mention detection
  const handleCommentChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    setNewComment(value);
    
    // Detect @mention
    const lastAtSymbol = value.lastIndexOf('@');
    if (lastAtSymbol !== -1) {
      const searchTerm = value.slice(lastAtSymbol + 1);
      if (searchTerm.length > 0 && !searchTerm.includes(' ')) {
        const filtered = allUsers
          .filter(user => 
            user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            user.email.toLowerCase().includes(searchTerm.toLowerCase())
          )
          .slice(0, 5)
          .map(user => user.name);
        setMentionSuggestions(filtered);
        setShowMentions(true);
        return;
      }
    }
    setShowMentions(false);
  };

  const insertMention = (name: string) => {
    const lastAtSymbol = newComment.lastIndexOf('@');
    const beforeMention = newComment.slice(0, lastAtSymbol);
    const afterMention = newComment.slice(lastAtSymbol + newComment.slice(lastAtSymbol).split(' ')[0].length);
    setNewComment(`${beforeMention}@${name} ${afterMention}`);
    setShowMentions(false);
    textareaRef.current?.focus();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newComment.trim()) {
      showError('CANNOT_COMMENT_ON_COMPLETED', 'Please enter a comment');
      return;
    }

    await guard(async () => {
      setSubmitting(true);
      await tasksApi.addComment(taskId, { comment: newComment });
      setNewComment('');
      showSuccess('Comment Added', 'Your comment has been posted successfully');
      onRefresh();
      setSubmitting(false);
    }, 'CANNOT_COMMENT_ON_COMPLETED');
  };

  const handleEdit = async (commentId: string) => {
    if (!editText.trim()) return;

    await guard(async () => {
      await tasksApi.updateComment(commentId, { comment: editText });
      setEditingId(null);
      setEditText('');
      showSuccess('Comment Updated', 'Your comment has been updated successfully');
      onRefresh();
    }, 'CANNOT_EDIT_OTHERS_COMMENT');
  };

  const handleDelete = async (commentId: string) => {
    if (window.confirm('Are you sure you want to delete this comment?')) {
      await guard(async () => {
        await tasksApi.deleteComment(commentId);
        showSuccess('Comment Deleted', 'Your comment has been deleted successfully');
        onRefresh();
      }, 'CANNOT_EDIT_OTHERS_COMMENT');
    }
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes} minute${minutes !== 1 ? 's' : ''} ago`;
    if (hours < 24) return `${hours} hour${hours !== 1 ? 's' : ''} ago`;
    if (days < 7) return `${days} day${days !== 1 ? 's' : ''} ago`;
    return date.toLocaleDateString();
  };

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, [newComment]);

  return (
    <div className="bg-white rounded-xl shadow-md p-5 mb-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-gradient-to-r from-yellow-500 to-orange-600 rounded-lg flex items-center justify-center">
            <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-gray-900">Comments</h3>
          <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded-full">
            {comments.length}
          </span>
        </div>
        
        {/* Quick stats */}
        {comments.length > 0 && (
          <div className="text-xs text-gray-400">
            Last activity: {formatDate(comments[comments.length - 1]?.created_at)}
          </div>
        )}
      </div>
      
      {/* Add Comment Form */}
      <form onSubmit={handleSubmit} className="mb-6 relative">
        <div className="relative">
          <textarea
            ref={textareaRef}
            value={newComment}
            onChange={handleCommentChange}
            placeholder="Write a comment... (Use @ to mention someone)"
            rows={3}
            className="w-full px-4 py-3 pr-24 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all resize-none"
          />
          
          {/* Action buttons */}
          <div className="absolute bottom-3 right-3 flex gap-2">
            <button
              type="button"
              onClick={() => setShowEmojiPicker(!showEmojiPicker)}
              className="p-1.5 text-gray-400 hover:text-yellow-500 transition-colors"
              title="Add emoji"
            >
              😊
            </button>
          </div>
        </div>
        
        {/* Mention Suggestions */}
        {showMentions && mentionSuggestions.length > 0 && (
          <div className="absolute z-10 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden">
            {mentionSuggestions.map((name, idx) => (
              <button
                key={idx}
                type="button"
                onClick={() => insertMention(name)}
                className="w-full text-left px-4 py-2 text-sm hover:bg-blue-50 transition-colors flex items-center gap-2"
              >
                <span className="text-blue-500">@</span>
                <span className="font-medium text-gray-700">{name}</span>
              </button>
            ))}
          </div>
        )}
        
        <div className="flex justify-end mt-2">
          <button 
            type="submit" 
            disabled={submitting || !newComment.trim()}
            className="px-5 py-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all disabled:opacity-50 font-medium shadow-sm flex items-center gap-2"
          >
            {submitting ? (
              <>
                <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Posting...
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                </svg>
                Post Comment
              </>
            )}
          </button>
        </div>
      </form>

      {/* Comments List */}
      <div className="space-y-4 max-h-96 overflow-y-auto">
        {comments.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <svg className="w-16 h-16 mx-auto text-gray-300 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
            <p className="text-sm font-medium">No comments yet</p>
            <p className="text-xs mt-1">Be the first to start the conversation</p>
          </div>
        ) : (
          comments.map((comment) => {
            const isCurrentUser = comment.user_id === currentUser?.id;
            const userName = getUserName(comment.user_id);
            const userEmail = getUserEmail(comment.user_id);
            const isTaskCreator = comment.user_id === taskId;
            
            return (
              <div 
                key={comment.id} 
                className={`p-4 rounded-xl transition-all duration-200 ${
                  isCurrentUser 
                    ? 'bg-blue-50/30 border border-blue-100' 
                    : 'bg-gray-50 hover:bg-gray-100'
                }`}
              >
                <div className="flex justify-between items-start mb-2">
                  <div className="flex items-center gap-3">
                    {/* Avatar */}
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-bold shadow-sm ${
                      isCurrentUser 
                        ? 'bg-gradient-to-br from-blue-500 to-blue-600' 
                        : 'bg-gradient-to-br from-gray-500 to-gray-600'
                    }`}>
                      {userName.charAt(0).toUpperCase()}
                    </div>
                    
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-gray-900">
                          {userName}
                        </span>
                        {isCurrentUser && (
                          <span className="text-xs text-blue-600 bg-blue-100 px-2 py-0.5 rounded-full font-medium">
                            You
                          </span>
                        )}
                        {isTaskCreator && !isCurrentUser && (
                          <span className="text-xs text-purple-600 bg-purple-100 px-2 py-0.5 rounded-full font-medium">
                            Task Creator
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 text-xs text-gray-500 mt-0.5">
                        <span>{formatDate(comment.created_at)}</span>
                        {comment.edited_at && (
                          <span className="italic text-gray-400">(edited)</span>
                        )}
                        {userEmail && (
                          <>
                            <span className="text-gray-300">•</span>
                            <span className="text-gray-400">{userEmail}</span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  {isCurrentUser && (
                    <div className="flex gap-1">
                      <button 
                        onClick={() => {
                          setEditingId(comment.id);
                          setEditText(comment.comment);
                        }}
                        className="p-1.5 text-gray-400 hover:text-blue-600 transition-colors rounded-lg hover:bg-blue-50"
                        title="Edit"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                      </button>
                      <button 
                        onClick={() => handleDelete(comment.id)}
                        className="p-1.5 text-gray-400 hover:text-red-600 transition-colors rounded-lg hover:bg-red-50"
                        title="Delete"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  )}
                </div>
                
                {editingId === comment.id ? (
                  <div className="mt-3 ml-13">
                    <textarea
                      value={editText}
                      onChange={(e) => setEditText(e.target.value)}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      autoFocus
                    />
                    <div className="flex gap-2 mt-2">
                      <button 
                        onClick={() => handleEdit(comment.id)}
                        className="px-3 py-1.5 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 transition-colors"
                      >
                        Save Changes
                      </button>
                      <button 
                        onClick={() => setEditingId(null)}
                        className="px-3 py-1.5 border border-gray-300 text-gray-700 text-sm rounded-lg hover:bg-gray-50 transition-colors"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="mt-2 ml-13">
                    <p className="text-gray-700 leading-relaxed">
                      {comment.comment.split(/(@\w+)/g).map((part, idx) => {
                        if (part.startsWith('@')) {
                          return (
                            <span key={idx} className="text-blue-600 font-medium bg-blue-50 px-1 rounded">
                              {part}
                            </span>
                          );
                        }
                        return part;
                      })}
                    </p>
                    
                    {/* Reply button */}
                    <button 
                      onClick={() => {
                        setNewComment(`@${userName} `);
                        textareaRef.current?.focus();
                      }}
                      className="mt-2 text-xs text-gray-400 hover:text-blue-600 transition-colors flex items-center gap-1"
                    >
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                      </svg>
                      Reply
                    </button>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

export default CommentSection;